import { useMemo, useState } from 'react';
import {
  Card, Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow, Text,
} from '@tremor/react';
import type { StaffRow } from '../api/types';
import { t } from '../lib/i18n';

type SortKey = 'name' | 'tickets' | 'revenue_mxn' | 'margin_mxn' | 'avgTicket' | 'returns_mxn' | 'netMargin';

const mxn = (n: number) =>
  n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 });

interface Props {
  rows: StaffRow[];
  onRowClick: (r: StaffRow) => void;
}

export default function SalespeopleTable({ rows, onRowClick }: Props) {
  const [sortKey, setSortKey] = useState<SortKey>('netMargin');
  const [asc, setAsc] = useState(false);

  const decorated = useMemo(
    () =>
      rows.map((r) => ({
        ...r,
        avgTicket: r.tickets ? r.revenue_mxn / r.tickets : 0,
        netMargin: r.margin_mxn - r.returns_margin_mxn,
      })),
    [rows],
  );

  const sorted = useMemo(() => {
    const assigned = decorated.filter((r) => !r.is_unassigned);
    const unassigned = decorated.filter((r) => r.is_unassigned);
    const cmp = (a: typeof decorated[number], b: typeof decorated[number]) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      if (typeof va === 'string' && typeof vb === 'string') {
        return asc ? va.localeCompare(vb) : vb.localeCompare(va);
      }
      return asc ? (va as number) - (vb as number) : (vb as number) - (va as number);
    };
    return [...assigned.sort(cmp), ...unassigned];
  }, [decorated, sortKey, asc]);

  const click = (k: SortKey) => () => {
    if (sortKey === k) setAsc((v) => !v);
    else {
      setSortKey(k);
      setAsc(false);
    }
  };

  if (rows.length === 0) {
    return (
      <Card>
        <Text>{t.salespeople.empty}</Text>
      </Card>
    );
  }

  const headerBtn = (label: string, key: SortKey, align: 'left' | 'right' = 'right') => (
    <button
      type="button"
      onClick={click(key)}
      className={
        (align === 'right' ? 'w-full text-right ' : '') +
        'hover:underline ' +
        (sortKey === key ? 'text-tremor-brand font-semibold' : '')
      }
    >
      {label}
    </button>
  );

  return (
    <Card className="p-0 overflow-hidden">
      <Table>
        <TableHead>
          <TableRow>
            <TableHeaderCell>{headerBtn(t.salespeople.cols.name, 'name', 'left')}</TableHeaderCell>
            <TableHeaderCell className="text-right">{headerBtn(t.salespeople.cols.tickets, 'tickets')}</TableHeaderCell>
            <TableHeaderCell className="text-right">{headerBtn(t.salespeople.cols.revenue, 'revenue_mxn')}</TableHeaderCell>
            <TableHeaderCell className="text-right">{headerBtn(t.salespeople.cols.margin, 'margin_mxn')}</TableHeaderCell>
            <TableHeaderCell className="text-right">{headerBtn(t.salespeople.cols.avgTicket, 'avgTicket')}</TableHeaderCell>
            <TableHeaderCell className="text-right">{headerBtn(t.salespeople.cols.returns, 'returns_mxn')}</TableHeaderCell>
            <TableHeaderCell className="text-right">{headerBtn(t.salespeople.cols.netMargin, 'netMargin')}</TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((r) => (
            <TableRow
              key={`${r.staff_id}-${r.is_unassigned}`}
              className={
                r.is_unassigned
                  ? 'border-t-2 border-tremor-border italic text-tremor-content'
                  : 'cursor-pointer hover:bg-tremor-background-muted'
              }
              onClick={() => !r.is_unassigned && onRowClick(r)}
            >
              <TableCell>{r.name}</TableCell>
              <TableCell className="text-right">{r.tickets}</TableCell>
              <TableCell className="text-right">{mxn(r.revenue_mxn)}</TableCell>
              <TableCell className="text-right">{mxn(r.margin_mxn)}</TableCell>
              <TableCell className="text-right">{mxn(r.avgTicket)}</TableCell>
              <TableCell className="text-right">{mxn(r.returns_mxn)}</TableCell>
              <TableCell className="text-right">{mxn(r.netMargin)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}
